import { X } from '@phosphor-icons/react';
import { HttpStatusCode } from 'axios';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { AccentButton, NeutralButton } from '../../../../../../shared/Buttons';
import { Entity } from '../../../../../../types/apiTypes';
import useApi from '../../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../../utils/UserContext';
import { Input } from 'antd';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

// Define the form data type
interface InvitePartnerAgentFormData {
  firstName: string;
  lastName: string;
  email: string;
}

export const InvitePartnerAgentModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  // Add this inside your component
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<InvitePartnerAgentFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
  });

  // Watch all form fields to check if form is complete
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const email = watch('email');

  // Check if all required fields have values - use explicit boolean checks
  const isFormComplete =
    Boolean(firstName) && Boolean(lastName) && Boolean(email);

  // Form submission handler
  const onSubmit = (data: InvitePartnerAgentFormData) => {
    // Handle form submission logic here
    sendInvite({
      partner_id: userId ?? '',
      email: data.email,
      first_name: data.firstName,
      last_name: data.lastName,
    });
  };

  const sendInvite = async ({
    partner_id,
    email,
    first_name,
    last_name,
  }: {
    partner_id: string;
    email: string;
    first_name: string;
    last_name: string;
  }) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        `/partnerAgentInvites`,
        'post',
        {
          partner_id: partner_id,
          email: email,
          first_name: first_name,
          last_name: last_name,
        },
        'Error occurred while sending agent invite.'
      );
      if (response?.status === HttpStatusCode.Created) {
        toast.success('Successfully sent agent invite.');
        await queryClient.invalidateQueries({
          queryKey: ['partnerAgentsAndInvites'],
        });
      }
    } catch (e: any) {
      console.error(
        'Error occurred while sending partnerAgent invite: ',
        e?.message
      );
    } finally {
      onClose();
      setLoading(false);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="fixed inset-0 z-[999] flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="h-auto max-h-[90%] w-full overflow-x-hidden rounded-[4px] bg-white sm:max-w-[90%] md:max-w-[60%] lg:max-w-[600px]">
              <div className="flex h-[56px] w-full flex-row items-center justify-between p-4 align-middle">
                <h2 className="text-[16px] font-[600] text-primary">
                  Invite Agent
                </h2>
                <X
                  className="hover:cursor-pointer hover:opacity-80"
                  onClick={onClose}
                  weight="regular"
                  size={24}
                />
              </div>
              {userRecord?.userType === Entity.Partner && (
                <form
                  className="flex h-full w-full flex-col items-center gap-6 overflow-y-auto overflow-x-hidden border-[1px] border-bordergray p-6"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  <div className="flex w-full flex-row items-center space-x-4 align-middle">
                    <p>Invite a new agent to your company.</p>
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <Input
                      placeholder="Enter first name"
                      className="h-[48px] w-full"
                      {...register('firstName', {
                        required: 'First name is required',
                      })}
                      onChange={(e) => setValue('firstName', e.target.value)}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <Input
                      placeholder="Enter last name"
                      className="h-[48px] w-full"
                      {...register('lastName', {
                        required: 'Last name is required',
                      })}
                      onChange={(e) => setValue('lastName', e.target.value)}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>

                  <div className="w-full">
                    <label className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter email address"
                      className="h-[48px] w-full"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Invalid email address',
                        },
                      })}
                      onChange={(e) => setValue('email', e.target.value)}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </form>
              )}
              {/* Buttons outside the form */}
              <div className="flex justify-between space-x-2 p-4">
                <NeutralButton
                  onClick={onClose}
                  addClass="rounded-full w-[238px] h-[48px] flex flex-row items-center justify-center"
                >
                  Cancel
                </NeutralButton>
                <AccentButton
                  submit={true}
                  onClick={handleSubmit(onSubmit)}
                  disabled={!isFormComplete}
                  addClass="rounded-full hover:bg-yellow-600 w-[238px] h-[48px] disabled:opacity-50 disabled:bg-yellow-600 flex flex-row items-center justify-center"
                >
                  {!loading ? (
                    'Send Invite'
                  ) : (
                    <ClipLoader
                      color="#1D213B"
                      loading={loading}
                      size={20}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="h-full w-full justify-center"
                    />
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  if (!isOpen) return null;

  return <>{renderScreenView(screenView)}</>;
};

export default InvitePartnerAgentModal;
